import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PERMISSIONS as P } from './constants/permissions';
import { ROLES } from './constants/roles';
import Layout from './layouts/Layout';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import Dashboard from './pages/Dashboard';
import Doctors from './pages/Doctors';
import DoctorDetail from './pages/DoctorDetail';
import Patients from './pages/Patients';
import PatientDetail from './pages/PatientDetail';
import Appointments from './pages/Appointments';
import Inventory from './pages/Inventory';
import Orders from './pages/Orders';
import Payments from './pages/Payments';
import Specializations from './pages/Specializations';
import TimeSlots from './pages/TimeSlots';
import LabTests from './pages/LabTests';
import LabTestDetail from './pages/LabTestDetail';
import LabTestCategories from './pages/LabTestCategories';
import LabBookings from './pages/LabBookings';
import LabBookingDetail from './pages/LabBookingDetail';
import LabPayments from './pages/LabPayments';
import SupportTickets from './pages/SupportTickets';
import SupportTicketDetail from './pages/SupportTicketDetail';
import DeliveryBoys from './pages/DeliveryBoys';
import DeliveryBoyDetail from './pages/DeliveryBoyDetail';
import WithdrawRequests from './pages/WithdrawRequests';
import DepositRequests from './pages/DepositRequests';
import Settings from './pages/Settings';
import PolicyHomePage from './pages/Policy/PolicyHomePage';


/* ── Route guard: must be logged in ──────────────────────────────────────── */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

/* ── Route guard: must be logged in AND have permission ──────────────────── */
const PermissionRoute = ({ permissionId, children }) => {
  const { isAuthenticated, hasPermission } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasPermission(permissionId)) return <Navigate to="/unauthorized" replace />;
  return children;
};

/* ── Default landing page per role ───────────────────────────────────────── */
const DEFAULT_LANDING = {
  [ROLES.ADMIN]:    '/',
  [ROLES.LAB]:      '/lab-bookings',
  [ROLES.PHARMACY]: '/inventory',
  [ROLES.SUPPORT]:  '/support',
};

/* ── Index route: admin → dashboard, others → their default page ─────────── */
const IndexRoute = () => {
  const { isAuthenticated, hasPermission, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (hasPermission(P.DASHBOARD_VIEW)) return <Dashboard />;
  const landing = DEFAULT_LANDING[user?.roleId];
  return landing ? <Navigate to={landing} replace /> : <Navigate to="/unauthorized" replace />;
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Dashboard — Admin only */}
        <Route index element={<IndexRoute />} />

        {/* Doctors */}
        <Route path="doctors"     element={<PermissionRoute permissionId={P.DOCTORS_VIEW}><Doctors /></PermissionRoute>} />
        <Route path="doctors/:id" element={<PermissionRoute permissionId={P.DOCTORS_VIEW}><DoctorDetail /></PermissionRoute>} />

        {/* Patients */}
        <Route path="patients"     element={<PermissionRoute permissionId={P.PATIENTS_VIEW}><Patients /></PermissionRoute>} />
        <Route path="patients/:id" element={<PermissionRoute permissionId={P.PATIENTS_VIEW}><PatientDetail /></PermissionRoute>} />

        {/* Appointments */}
        <Route path="appointments" element={<PermissionRoute permissionId={P.APPOINTMENTS_VIEW}><Appointments /></PermissionRoute>} />

        {/* Inventory */}
        <Route path="inventory" element={<PermissionRoute permissionId={P.INVENTORY_VIEW}><Inventory /></PermissionRoute>} />

        {/* Orders */}
        <Route path="orders" element={<PermissionRoute permissionId={P.ORDERS_VIEW}><Orders /></PermissionRoute>} />

        {/* Payments */}
        <Route path="payments" element={<PermissionRoute permissionId={P.PAYMENTS_VIEW}><Payments /></PermissionRoute>} />

        {/* Master Management */}
        <Route path="master/specializations" element={<PermissionRoute permissionId={P.MASTER_VIEW}><Specializations /></PermissionRoute>} />
        <Route path="master/time-slots"      element={<PermissionRoute permissionId={P.MASTER_VIEW}><TimeSlots /></PermissionRoute>} />

        {/* Lab */}
        <Route path="lab-tests"           element={<PermissionRoute permissionId={P.LAB_TESTS_VIEW}><LabTests /></PermissionRoute>} />
        <Route path="lab-tests/:id"       element={<PermissionRoute permissionId={P.LAB_TESTS_VIEW}><LabTestDetail /></PermissionRoute>} />
        <Route path="lab-test-categories" element={<PermissionRoute permissionId={P.LAB_CATEGORIES_VIEW}><LabTestCategories /></PermissionRoute>} />
        <Route path="lab-bookings"        element={<PermissionRoute permissionId={P.LAB_BOOKINGS_VIEW}><LabBookings /></PermissionRoute>} />
        <Route path="lab-bookings/:id"    element={<PermissionRoute permissionId={P.LAB_BOOKINGS_VIEW}><LabBookingDetail /></PermissionRoute>} />
        <Route path="lab-payments"        element={<PermissionRoute permissionId={P.LAB_PAYMENTS_VIEW}><LabPayments /></PermissionRoute>} />

        {/* Support */}
        <Route path="support"     element={<PermissionRoute permissionId={P.SUPPORT_VIEW}><SupportTickets /></PermissionRoute>} />
        <Route path="support/:id" element={<PermissionRoute permissionId={P.SUPPORT_VIEW}><SupportTicketDetail /></PermissionRoute>} />

        {/* Delivery */}
        <Route path="delivery-boys"     element={<PermissionRoute permissionId={P.DELIVERY_VIEW}><DeliveryBoys /></PermissionRoute>} />
        <Route path="delivery-boys/:id" element={<PermissionRoute permissionId={P.DELIVERY_VIEW}><DeliveryBoyDetail /></PermissionRoute>} />

        {/* Wallet */}
        <Route path="withdraw-requests" element={<PermissionRoute permissionId={P.WITHDRAW_VIEW}><WithdrawRequests /></PermissionRoute>} />
        <Route path="deposit-requests"  element={<PermissionRoute permissionId={P.DEPOSIT_VIEW}><DepositRequests /></PermissionRoute>} />

        {/* Settings */}
        <Route path="settings" element={<PermissionRoute permissionId={P.SETTINGS_VIEW}><Settings /></PermissionRoute>} />
        <Route path="policy" element={<PermissionRoute permissionId={P.SETTINGS_VIEW}><PolicyHomePage/></PermissionRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: { borderRadius: '10px', fontSize: '14px' },
          success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error:   { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }}
      />
      <AppRoutes />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
